/**
 * Chat Files Database Helpers
 *
 * Loads and persists file links for chats.
 *
 * Primary responsibilities:
 * - Route browser chat file reads through authenticated app routes
 * - Create chat-to-file join rows
 *
 * Dependencies:
 * - Supabase chat and chat_files tables
 */

import { TablesInsert } from '@/supabase/types'
import type { SupabaseClient } from '@supabase/supabase-js'

import { createServiceClient } from '@/lib/supabase/service-client'
import { supabase } from '@/lib/supabase/browser-client'
import type { Database } from '@/supabase/types'

type AuthenticatedFetch = typeof fetch

const getAuthenticatedFetch = (): AuthenticatedFetch => {
  const mod = require('@/lib/auth/authenticated-fetch') as {
    authenticatedFetch: AuthenticatedFetch
  }

  return mod.authenticatedFetch
}

async function readJsonError(response: Response, fallbackMessage: string) {
  const responseText = await response.text().catch(() => '')

  if (!responseText) {
    return fallbackMessage
  }

  try {
    const payload = JSON.parse(responseText) as { error?: string }
    return payload.error || fallbackMessage
  } catch {
    return responseText
  }
}

function getDefaultClient(passed?: SupabaseClient<Database>) {
  if (passed) return passed
  if (typeof window === 'undefined') return createServiceClient()
  return supabase as SupabaseClient<Database>
}

async function readBrowserChatFilesByChatId(chatId: string) {
  const response = await getAuthenticatedFetch()(`/api/chat/${encodeURIComponent(chatId)}/files`, {
    cache: 'no-store',
    credentials: 'include'
  })

  if (!response.ok) {
    throw new Error(await readJsonError(response, 'Failed to load chat files'))
  }

  return await response.json()
}

async function createBrowserChatFiles(chatFiles: TablesInsert<'chat_files'>[]) {
  if (chatFiles.length === 0) {
    return []
  }

  const chatId = chatFiles[0]?.chat_id?.trim()

  if (!chatId) {
    throw new Error('chat_id required')
  }

  if (chatFiles.some((chatFile) => chatFile.chat_id !== chatId)) {
    throw new Error('Browser chat file batch must target a single chat')
  }

  const response = await getAuthenticatedFetch()(`/api/chat/${encodeURIComponent(chatId)}/files`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    credentials: 'include',
    body: JSON.stringify({ chatFiles })
  })

  if (!response.ok) {
    throw new Error(await readJsonError(response, 'Failed to create chat files'))
  }

  return await response.json()
}

export const getChatFilesByChatId = async (
  chatId: string,
  client?: SupabaseClient<Database>
) => {
  if (!client && typeof window !== 'undefined') {
    return readBrowserChatFilesByChatId(chatId)
  }

  const db = getDefaultClient(client)
  const { data: chatFiles, error } = await db
    .from('chats')
    .select(
      `
      id, 
      name, 
      files (*)
    `
    )
    .eq('id', chatId)
    .single()

  if (!chatFiles) {
    throw new Error(error.message)
  }

  return chatFiles
}

export const createChatFile = async (chatFile: TablesInsert<'chat_files'>) => {
  if (typeof window !== 'undefined') {
    return createBrowserChatFiles([chatFile])
  }

  const { data: createdChatFile, error } = await supabase
    .from('chat_files')
    .insert(chatFile)
    .select('*')

  if (!createdChatFile) {
    throw new Error(error.message)
  }

  return createdChatFile
}

export const createChatFiles = async (
  chatFiles: TablesInsert<'chat_files'>[],
  client?: SupabaseClient<Database>
) => {
  if (!client && typeof window !== 'undefined') {
    return createBrowserChatFiles(chatFiles)
  }

  const db = client ?? getDefaultClient()
  const { data: createdChatFiles, error } = await db
    .from('chat_files')
    .insert(chatFiles)
    .select('*')

  if (!createdChatFiles) {
    throw new Error(error.message)
  }

  return createdChatFiles
}
